import React, { useState, useMemo } from 'react';
import HrnbModal from '../controls/HrnbModal';
import HrnbButton from '../controls/HrnbButton';
import HrnbAlert from '../controls/HrnbAlert';
import HrnbSearch from '../controls/HrnbSearch';
import HrnbLoader from '../controls/HrnbLoader';
import '../../css/components/RoundParticipantsManager.css';

/**
 * Компонент управления участниками раунда
 * Позволяет добавлять/удалять участников из раунда
 * 
 * @component
 * @param {Object} props
 * @param {boolean} props.isOpen - Открыто ли модальное окно
 * @param {Function} props.onClose - Обработчик закрытия
 * @param {Object} props.round - Данные раунда
 * @param {string} props.battleId - UUID баттла
 * @param {Array} props.battleParticipants - Все участники баттла
 * @param {Function} props.onAddParticipant - Обработчик добавления участника
 * @param {Function} props.onRemoveParticipant - Обработчик удаления участника
 * @param {Object} props.loading - Состояние загрузки действий
 */
export default function RoundParticipantsManager({
  isOpen,
  onClose,
  round,
  battleId,
  battleParticipants = [],
  onAddParticipant,
  onRemoveParticipant,
  loading = {}
}) {
  const [searchQuery, setSearchQuery] = useState('');

  // Получаем IDs текущих участников раунда
  const roundParticipantIds = useMemo(() => {
    return new Set((round?.participants || []).map(p => p.id));
  }, [round]);

  // Разделяем участников на тех, кто в раунде и тех, кого можно добавить
  const { inRound, available } = useMemo(() => {
    const inRoundList = [];
    const availableList = [];

    battleParticipants.forEach(participant => {
      const isInRound = roundParticipantIds.has(participant.id);
      const matchesSearch = searchQuery.trim() === '' || 
        participant.nickname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        participant.displayName?.toLowerCase().includes(searchQuery.toLowerCase());

      if (matchesSearch) {
        if (isInRound) {
          inRoundList.push(participant);
        } else {
          availableList.push(participant);
        }
      }
    });

    return {
      inRound: inRoundList,
      available: availableList
    };
  }, [battleParticipants, roundParticipantIds, searchQuery]);

  const handleAdd = (personId) => {
    if (round?.id) {
      onAddParticipant?.(round.id, personId);
    }
  };

  const handleRemove = (personId) => {
    if (round?.id) {
      onRemoveParticipant?.(round.id, personId);
    }
  };

  return (
    <HrnbModal
      open={isOpen}
      onClose={onClose}
      title={`Участники раунда: ${round?.title || ''}`}
      size="large"
    >
      <div className="round-participants-manager">
        {/* Информация о раунде */}
        {round && (
          <div className="round-info">
            <div className="round-info-item">
              <span className="label">Тип:</span>
              <span className="value">{getRoundTypeLabel(round.type)}</span>
            </div>
            <div className="round-info-item">
              <span className="label">Участников:</span>
              <span className="value">{inRound.length} из {battleParticipants.length}</span>
            </div>
          </div>
        )}

        {/* Поиск */}
        <div className="search-section">
          <HrnbSearch
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Поиск участника по имени..."
            className="participants-search"
          />
        </div>

        {/* Участники в раунде */}
        <div className="participants-section">
          <h4 className="section-title">
            📋 В раунде ({inRound.length})
          </h4>
          {inRound.length === 0 ? (
            <HrnbAlert variant="info">
              В раунде пока нет участников
            </HrnbAlert>
          ) : (
            <div className="participants-list">
              {inRound.map(participant => (
                <div
                  key={participant.id}
                  className="participant-item participant-item--in-round"
                >
                  <div className="participant-info">
                    <div className="participant-name">
                      {participant.nickname || participant.displayName}
                    </div>
                    {participant.city && (
                      <div className="participant-city">{participant.city}</div>
                    )}
                  </div>
                  <HrnbButton
                    variant="danger"
                    size="sm"
                    onClick={() => handleRemove(participant.id)}
                    disabled={loading[`remove-participant-${round?.id}-${participant.id}`]}
                  >
                    {loading[`remove-participant-${round?.id}-${participant.id}`] ? (
                      <HrnbLoader size="sm" />
                    ) : (
                      'Удалить'
                    )}
                  </HrnbButton>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Доступные участники */}
        <div className="participants-section">
          <h4 className="section-title">
            ➕ Добавить участника ({available.length})
          </h4>
          {available.length === 0 ? (
            <HrnbAlert variant="info">
              {battleParticipants.length === 0
                ? 'В баттле нет участников. Сначала добавьте участников в баттл.'
                : searchQuery
                  ? 'Никто не найден по вашему запросу'
                  : 'Все участники уже добавлены в раунд'}
            </HrnbAlert>
          ) : (
            <div className="participants-list">
              {available.map(participant => (
                <div
                  key={participant.id}
                  className="participant-item participant-item--available"
                >
                  <div className="participant-info">
                    <div className="participant-name">
                      {participant.nickname || participant.displayName}
                    </div>
                    {participant.city && (
                      <div className="participant-city">{participant.city}</div>
                    )}
                  </div>
                  <HrnbButton
                    variant="primary"
                    size="sm"
                    onClick={() => handleAdd(participant.id)}
                    disabled={loading[`add-participant-${round?.id}-${participant.id}`]}
                  >
                    {loading[`add-participant-${round?.id}-${participant.id}`] ? (
                      <HrnbLoader size="sm" />
                    ) : (
                      'Добавить'
                    )}
                  </HrnbButton>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Кнопки управления */}
        <div className="modal-actions">
          <HrnbButton variant="outline" onClick={onClose}>
            Закрыть
          </HrnbButton>
        </div>
      </div>
    </HrnbModal>
  );
}

function getRoundTypeLabel(type) {
  const labels = {
    QUALIFYING: 'Отборочный',
    DRAW: 'Жеребьёвка',
    PAIR: 'Парный'
  };
  return labels[type] || type;
}
